import React from 'react';
import { Button, Image, Space, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { MediaOrUrlField } from '@shared/components/MediaOrUrlField';

interface MultiImageFieldProps {
  value?: string[];
  onChange?: (v: string[]) => void;
  max?: number;
}

/**
 * Campo multi-immagine per i prodotti: ogni slot usa MediaOrUrlField, quindi
 * l'operatore può CARICARE un file (repository media / S3 già in uso) oppure
 * incollare un URL. Retrocompatibile: i prodotti esistenti con URL vengono
 * caricati negli slot e restano modificabili. Emette sempre string[].
 */
export function MultiImageField({ value, onChange, max = 10 }: MultiImageFieldProps): React.ReactElement {
  const images = value ?? [];

  const setAt = (i: number, url: string | null): void => {
    const next = [...images];
    next[i] = url ?? '';
    onChange?.(next);
  };
  const removeAt = (i: number): void => onChange?.(images.filter((_, idx) => idx !== i));
  const add = (): void => onChange?.([...images, '']);

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={12}>
      {images.map((img, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', width: '100%' }}>
          {img ? (
            <Image
              src={img}
              width={56}
              height={56}
              style={{ objectFit: 'cover', borderRadius: 6, flex: '0 0 auto' }}
              fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='56' height='56'%3E%3C/svg%3E"
            />
          ) : (
            <div
              style={{
                width: 56,
                height: 56,
                flex: '0 0 auto',
                border: '1px dashed #d9d9d9',
                borderRadius: 6,
              }}
            />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <MediaOrUrlField category="OTHER" value={img} onChange={(url) => setAt(i, url)} />
          </div>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeAt(i)}
            aria-label={`Rimuovi immagine ${i + 1}`}
          />
        </div>
      ))}
      {images.length < max ? (
        <Button icon={<PlusOutlined />} onClick={add} block>
          Aggiungi immagine{images.length > 0 ? ` (${images.length}/${max})` : ''}
        </Button>
      ) : (
        <Typography.Text type="secondary">Raggiunto il massimo di {max} immagini.</Typography.Text>
      )}
    </Space>
  );
}
